import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { DollarSign, TrendingUp, TrendingDown, Star, Trophy, Target, Sparkles } from 'lucide-react';

interface BudgetProgressProps {
  category: 'needs' | 'wants' | 'savings';
  allocated: number;
  spent: number;
  total: number;
}

export const BudgetProgress: React.FC<BudgetProgressProps> = ({
  category,
  allocated,
  spent,
  total
}) => {
  const percentage = (spent / allocated) * 100;
  const remaining = allocated - spent;
  const isOverBudget = spent > allocated;
  const isSavingsCategory = category === 'savings';
  const savingsBonus = isSavingsCategory && spent > allocated;

  const getStatusColor = () => {
    if (isOverBudget && !isSavingsCategory) return 'text-red-500';
    if (savingsBonus) return 'text-amber-500';
    if (percentage > 90) return 'text-amber-500';
    return 'text-emerald-500';
  };

  const getProgressColor = () => {
    if (isOverBudget && !isSavingsCategory) return 'bg-red-500';
    if (savingsBonus) return 'bg-gradient-to-r from-emerald-500 via-amber-500 to-emerald-500';
    if (percentage > 90) return 'bg-amber-500';
    return 'bg-emerald-500';
  };

  const getSavingsMessage = () => {
    if (!isSavingsCategory) return null;
    if (savingsBonus) {
      const extraSavings = spent - allocated;
      const bonusPercentage = (extraSavings / allocated) * 100;
      return {
        icon: Trophy,
        title: 'Savings Champion! 🎉',
        message: `You've exceeded your savings goal by ${bonusPercentage.toFixed(1)}% (+$${extraSavings.toLocaleString()})`,
        color: 'text-amber-500'
      };
    }
    if (percentage >= 90) {
      return {
        icon: Star,
        title: 'Almost There! ⭐',
        message: `You're ${percentage.toFixed(1)}% of the way to your savings goal`,
        color: 'text-emerald-500'
      };
    }
    return {
      icon: Target,
      title: 'Keep Going! 🎯',
      message: `You're making progress towards your savings goal`,
      color: 'text-emerald-500'
    };
  };

  return (
    <div className="glass-card p-6 border border-emerald-500/20">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold capitalize">{category}</h3>
        <div className={`flex items-center gap-1 ${getStatusColor()}`}>
          {isOverBudget && !isSavingsCategory ? (
            <TrendingUp className="w-4 h-4" />
          ) : (
            <TrendingDown className="w-4 h-4" />
          )}
          <span className="text-sm">
            {isOverBudget && !isSavingsCategory ? 'Over Budget' : 
             savingsBonus ? 'Extra Savings!' : 'Under Budget'}
          </span>
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex justify-between items-baseline">
          <div className="space-y-1">
            <span className="text-sm text-slate-400">Spent</span>
            <p className="text-2xl font-bold">${spent.toLocaleString()}</p>
          </div>
          <div className="text-right space-y-1">
            <span className="text-sm text-slate-400">Allocated</span>
            <p className="text-2xl font-bold text-emerald-500">
              ${allocated.toLocaleString()}
            </p>
          </div>
        </div>

        <div className="space-y-2">
          <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ 
                width: `${Math.min(100, percentage)}%`,
                background: savingsBonus ? [
                  'linear-gradient(90deg, #10b981, #f59e0b, #10b981)',
                  'linear-gradient(90deg, #f59e0b, #10b981, #f59e0b)',
                  'linear-gradient(90deg, #10b981, #f59e0b, #10b981)'
                ] : undefined
              }}
              transition={{ 
                duration: 0.5,
                ease: "easeOut",
                background: savingsBonus ? {
                  duration: 2,
                  repeat: Infinity,
                  ease: "linear"
                } : undefined
              }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              className={`h-full ${getProgressColor()}`}
            />
          </div>
          <div className="flex justify-between text-sm">
            <span className={getStatusColor()}>
              {percentage.toFixed(1)}% Used
            </span>
            <span className="text-slate-400">
              ${Math.abs(remaining).toLocaleString()} {isOverBudget && !isSavingsCategory ? 'Over' : 'Remaining'}
            </span>
          </div>
        </div>

        <AnimatePresence mode="wait">
          {isSavingsCategory && (
            <motion.div
              key={savingsBonus ? 'bonus' : 'normal'}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className={`mt-4 p-4 rounded-lg ${
                savingsBonus ? 'bg-gradient-to-r from-emerald-500/10 via-amber-500/10 to-emerald-500/10' : 'bg-emerald-500/10'
              }`}
            >
              {(() => {
                const message = getSavingsMessage();
                if (!message) return null;
                const Icon = message.icon;
                return (
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-lg bg-emerald-500/10">
                      <Icon className={`w-5 h-5 ${message.color}`} />
                    </div>
                    <div>
                      <h4 className={`font-medium ${message.color}`}>{message.title}</h4>
                      <p className="text-sm text-slate-300">{message.message}</p>
                    </div>
                  </div>
                );
              })()}
            </motion.div>
          )}
        </AnimatePresence>

        {!isSavingsCategory && (
          <div className="text-sm text-slate-400 mt-4">
            {isOverBudget ? (
              <p className="text-red-500">
                You've exceeded your {category} budget by ${Math.abs(remaining).toLocaleString()}
              </p>
            ) : (
              <p>
                You have ${remaining.toLocaleString()} left in your {category} budget
              </p>
            )}
          </div>
        )}

        {savingsBonus && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="absolute -top-2 -right-2"
          >
            <Sparkles className="w-6 h-6 text-amber-500" />
          </motion.div>
        )}
      </div>
    </div>
  );
};